import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export const useDashboard = () => {
  const { user } = useAuth();
  const { dashboardStats, statsLoading } = useAnalytics();
  const { currentWorkspace, contextStatus } = useWorkspace();

  const dashboardStatsQuery = useQuery({
    queryKey: ['dashboard-stats', currentWorkspace?.id],
    queryFn: async () => {
      if (!user?.id || !currentWorkspace?.id) throw new Error('User or workspace not available');
      
      // Get all relevant data in parallel
      const [partnersData, customersData, billsData, accountsData] = await Promise.all([
        supabase.from('partners').select('status').eq('user_id', user.id),
        supabase.from('customers').select('status').eq('workspace_id', currentWorkspace.id),
        supabase.from('bills').select('amount, processing_status, created_at').eq('workspace_id', currentWorkspace.id),
        supabase.from('accounts').select('status').eq('workspace_id', currentWorkspace.id),
      ]);

      if (partnersData.error) throw partnersData.error;
      if (customersData.error) throw customersData.error;
      if (billsData.error) throw billsData.error;
      if (accountsData.error) throw accountsData.error;

      const partners = partnersData.data || [];
      const customers = customersData.data || [];
      const bills = billsData.data || [];
      const accounts = accountsData.data || [];

      // Calculate KPIs
      const totalRevenue = bills.reduce((sum, bill) => sum + Number(bill.amount), 0);
      const totalBills = bills.length;
      const processedBills = bills.filter(bill => bill.processing_status === 'completed').length;
      const processingRate = totalBills > 0 ? (processedBills / totalBills) * 100 : 0;

      // Recent activity (last 7 days)
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      const recentBills = bills.filter(bill => new Date(bill.created_at) >= lastWeek);

      return {
        kpis: {
          totalRevenue,
          totalBills,
          processingRate,
          activePartners: partners.filter(p => p.status === 'active').length,
          activeCustomers: customers.filter(c => c.status === 'active').length,
          activeAccounts: accounts.filter(a => a.status === 'active').length,
        },
        recentActivity: recentBills.slice(0, 10).map(bill => ({
          type: 'bill_processed',
          message: `Bill processed for $${Number(bill.amount || 0).toFixed(2)}`,
          time: new Date(bill.created_at).toISOString(),
        })),
        quickStats: {
          totalPartners: partners.length,
          totalCustomers: customers.length,
          totalAccounts: accounts.length,
          pendingBills: bills.filter(bill => bill.processing_status === 'pending').length,
        },
      };
    },
    enabled: contextStatus === 'ready' && !!currentWorkspace?.id,
    retry: 0,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });

  return {
    stats: dashboardStatsQuery.data,
    isLoading: dashboardStatsQuery.isLoading,
    error: dashboardStatsQuery.error,
  };
};